import { matchPath } from "react-router-dom";
import { INNER_ROUTES } from "../constants/inner-routes";
import type {
  IRoutesMapping,
  TDashboardVersion
} from "../types/shared/cross-dashboards-routes";

const DEFAULT_ROUTE = 'home';

const OLD_DASHBOARD_ROUTES = {
  TRANSFERS: 'transfers',
  USERS: 'users',
  TRANSACTIONS: 'transactions',
  ORDERS: 'orders',
  BILLS: 'bills',
  CHARGE_BACKS: 'chargebacks',
  QUICK_LINKS: 'payment-links',
  NEW_PAYMENT_LINK: 'new-payment-link',
  REPORTS: 'reports',
  CAMPAIGNS: 'campaigns',
  SINGLE_CAMPAIGN: "campaigns/:id",
  CAMPAIGN_ANALYSIS: 'campaigns-stats',
  BUNDLES: 'bundles',
  DEVICES: 'devices',
  BRANCHES: 'branch',
  SUMMARY: 'reportPos',
  RISK_CENTER: 'riskCenter',
  SETTINGS: 'settings',
  CHECKOUT_CUSTOMIZATION: 'checkout-customization',
  PAYMENT_INTEGRATIONS: 'PaymentIntegrations',
  IFRAMES: 'iframes',
  XERO: 'xero',
} as const;

const NEW_DASHBOARD_ROUTES = {
  BALANCE: INNER_ROUTES.BALANCE,
  TRANSFERS: INNER_ROUTES.TRANSFERS,
  SINGLE_TRANSFER: INNER_ROUTES.SINGLE_TRANSFER,
  USERS: INNER_ROUTES.USER_AND_PERMISSIONS,
  MANAGE_ROLES: INNER_ROUTES.MANAGE_ROLES,
  CREATE_NEW_ROLE: INNER_ROUTES.CREATE_NEW_ROLE,
  USER_AND_PERMISSIONS: INNER_ROUTES.USER_AND_PERMISSIONS,
  ROLE_DETAILS: INNER_ROUTES.ROLE_DETAILS,
  TRANSACTIONS: INNER_ROUTES.TRANSACTIONS,
  SINGLE_TRANSACTION: INNER_ROUTES.SINGLE_TRANSACTION,
  ORDERS: INNER_ROUTES.ORDERS,
  SINGLE_ORDER: INNER_ROUTES.SINGLE_ORDER,
  BILLS: INNER_ROUTES.BILLS,
  SINGLE_BILL: INNER_ROUTES.SINGLE_BILL,
  CHARGE_BACKS: INNER_ROUTES.CHARGE_BACKS,
  SINGLE_CHARGE_BACK: INNER_ROUTES.SINGLE_CHARGE_BACK,
  QUICK_LINKS: INNER_ROUTES.QUICK_LINKS,
  SINGLE_QUICK_LINK: INNER_ROUTES.SINGLE_QUICK_LINK,
  NEW_PAYMENT_LINK: INNER_ROUTES.SINGLE_PAYMENT_LINK,
  BULK_PAYMENT_LINKS: INNER_ROUTES.BULK_PAYMENT_LINKS,
  SINGLE_PAYMENT_LINK: INNER_ROUTES.SINGLE_PAYMENT_LINK,
  REPORTS: INNER_ROUTES.REPORTS,
  CAMPAIGNS: INNER_ROUTES.CAMPAIGNS,
  CAMPAIGN_ANALYSIS: INNER_ROUTES.CAMPAIGN_ANALYSIS,
  SINGLE_CAMPAIGN: INNER_ROUTES.SINGLE_CAMPAIGN,
  BUNDLES: INNER_ROUTES.BUNDLES,
  DEVICES: INNER_ROUTES.DEVICES,
  BRANCHES: INNER_ROUTES.BRANCHES,
  SUMMARY: INNER_ROUTES.SUMMARY,
  RISK_CENTER: INNER_ROUTES.RISK_CENTER,
  SINGLE_RISK_ITEM: INNER_ROUTES.SINGLE_RISK_ITEM,
  SETTINGS: INNER_ROUTES.SETTINGS,
  PROFILE_DETAILS: INNER_ROUTES.PROFILE_DETAILS,
  CHANGE_PASSWORD: INNER_ROUTES.CHANGE_PASSWORD,
  BUSINESS_PROFILE: INNER_ROUTES.BUSINESS_PROFILE,
  NOTIFICATIONS: INNER_ROUTES.NOTIFICATIONS,
  SERVICES: INNER_ROUTES.SERVICES,
  API_KEYS: INNER_ROUTES.API_KEYS,
  CHECKOUT_CUSTOMIZATION: INNER_ROUTES.CHECKOUT_CUSTOMIZATION,
  PAYMENT_INTEGRATIONS: INNER_ROUTES.PAYMENT_INTEGRATIONS,
  SINGLE_PAYMENT_INTEGRATION: INNER_ROUTES.SINGLE_PAYMENT_INTEGRATION,
  IFRAMES: INNER_ROUTES.IFRAMES,
  XERO: INNER_ROUTES.XERO,
} as const;

const ROUTE_MAPPINGS: Record<string, IRoutesMapping> = {
  balance: {
    old: OLD_DASHBOARD_ROUTES.TRANSFERS,
    new: NEW_DASHBOARD_ROUTES.BALANCE,
  },
  transfers: {
    old: OLD_DASHBOARD_ROUTES.TRANSFERS,
    new: NEW_DASHBOARD_ROUTES.TRANSFERS,
  },
  single_transfer: {
    old: OLD_DASHBOARD_ROUTES.TRANSFERS,
    new: NEW_DASHBOARD_ROUTES.SINGLE_TRANSFER,
  },
  users: {
    old: OLD_DASHBOARD_ROUTES.USERS,
    new: NEW_DASHBOARD_ROUTES.USERS,
  },
  manage_roles: {
    old: OLD_DASHBOARD_ROUTES.USERS,
    new: NEW_DASHBOARD_ROUTES.MANAGE_ROLES,
  },
  create_new_role: {
    old: OLD_DASHBOARD_ROUTES.USERS,
    new: NEW_DASHBOARD_ROUTES.CREATE_NEW_ROLE,
  },
  role_details: {
    old: OLD_DASHBOARD_ROUTES.USERS,
    new: NEW_DASHBOARD_ROUTES.ROLE_DETAILS,
  },
  user_and_permissions: {
    old: OLD_DASHBOARD_ROUTES.USERS,
    new: NEW_DASHBOARD_ROUTES.USER_AND_PERMISSIONS,
  },
  transactions: {
    old: OLD_DASHBOARD_ROUTES.TRANSACTIONS,
    new: NEW_DASHBOARD_ROUTES.TRANSACTIONS,
  },
  single_transaction: {
    old: OLD_DASHBOARD_ROUTES.TRANSACTIONS,
    new: NEW_DASHBOARD_ROUTES.SINGLE_TRANSACTION,
  },
  orders: {
    old: OLD_DASHBOARD_ROUTES.ORDERS,
    new: NEW_DASHBOARD_ROUTES.ORDERS,
  },
  single_order: {
    old: OLD_DASHBOARD_ROUTES.ORDERS,
    new: NEW_DASHBOARD_ROUTES.SINGLE_ORDER,
  },
  bills: {
    old: OLD_DASHBOARD_ROUTES.BILLS,
    new: NEW_DASHBOARD_ROUTES.BILLS,
  },
  single_bill: {
    old: OLD_DASHBOARD_ROUTES.BILLS,
    new: NEW_DASHBOARD_ROUTES.SINGLE_BILL,
  },
  charge_backs: {
    old: OLD_DASHBOARD_ROUTES.CHARGE_BACKS,
    new: NEW_DASHBOARD_ROUTES.CHARGE_BACKS,
  },
  single_charge_back: {
    old: OLD_DASHBOARD_ROUTES.CHARGE_BACKS,
    new: NEW_DASHBOARD_ROUTES.SINGLE_CHARGE_BACK,
  },
  quick_links: {
    old: OLD_DASHBOARD_ROUTES.QUICK_LINKS,
    new: NEW_DASHBOARD_ROUTES.QUICK_LINKS,
  },
  single_quick_link: {
    old: OLD_DASHBOARD_ROUTES.QUICK_LINKS,
    new: NEW_DASHBOARD_ROUTES.SINGLE_QUICK_LINK,
  },
  new_payment_link: {
    old: OLD_DASHBOARD_ROUTES.NEW_PAYMENT_LINK,
    new: NEW_DASHBOARD_ROUTES.NEW_PAYMENT_LINK,
  },
  bulk_payment_links: {
    old: OLD_DASHBOARD_ROUTES.QUICK_LINKS,
    new: NEW_DASHBOARD_ROUTES.BULK_PAYMENT_LINKS,
  },
  single_payment_link: {
    old: OLD_DASHBOARD_ROUTES.QUICK_LINKS,
    new: NEW_DASHBOARD_ROUTES.SINGLE_PAYMENT_LINK,
  },
  campaigns: {
    old: OLD_DASHBOARD_ROUTES.CAMPAIGNS,
    new: NEW_DASHBOARD_ROUTES.CAMPAIGNS,
  },
  single_campaign: {
    old: OLD_DASHBOARD_ROUTES.SINGLE_CAMPAIGN,
    new: NEW_DASHBOARD_ROUTES.SINGLE_CAMPAIGN,
  },
  campaign_analysis: {
    old: OLD_DASHBOARD_ROUTES.CAMPAIGN_ANALYSIS,
    new: NEW_DASHBOARD_ROUTES.CAMPAIGN_ANALYSIS,
  },
  bundles: {
    old: OLD_DASHBOARD_ROUTES.BUNDLES,
    new: NEW_DASHBOARD_ROUTES.BUNDLES,
  },
  devices: {
    old: OLD_DASHBOARD_ROUTES.DEVICES,
    new: NEW_DASHBOARD_ROUTES.DEVICES,
  },
  branches: {
    old: OLD_DASHBOARD_ROUTES.BRANCHES,
    new: NEW_DASHBOARD_ROUTES.BRANCHES,
  },
  summary: {
    old: OLD_DASHBOARD_ROUTES.SUMMARY,
    new: NEW_DASHBOARD_ROUTES.SUMMARY,
  },
  reports: {
    old: OLD_DASHBOARD_ROUTES.REPORTS,
    new: NEW_DASHBOARD_ROUTES.REPORTS,
  },
  risk_center: {
    old: OLD_DASHBOARD_ROUTES.RISK_CENTER,
    new: NEW_DASHBOARD_ROUTES.RISK_CENTER,
  },
  single_risk_item: {
    old: OLD_DASHBOARD_ROUTES.RISK_CENTER,
    new: NEW_DASHBOARD_ROUTES.SINGLE_RISK_ITEM,
  },
  settings: {
    old: OLD_DASHBOARD_ROUTES.SETTINGS,
    new: NEW_DASHBOARD_ROUTES.SETTINGS,
  },
  profile_details: {
    old: OLD_DASHBOARD_ROUTES.SETTINGS,
    new: NEW_DASHBOARD_ROUTES.PROFILE_DETAILS,
  },
  change_password: {
    old: OLD_DASHBOARD_ROUTES.SETTINGS,
    new: NEW_DASHBOARD_ROUTES.CHANGE_PASSWORD,
  },
  business_profile: {
    old: OLD_DASHBOARD_ROUTES.SETTINGS,
    new: NEW_DASHBOARD_ROUTES.BUSINESS_PROFILE,
  },
  notifications: {
    old: OLD_DASHBOARD_ROUTES.SETTINGS,
    new: NEW_DASHBOARD_ROUTES.NOTIFICATIONS,
  },
  services: {
    old: OLD_DASHBOARD_ROUTES.SETTINGS,
    new: NEW_DASHBOARD_ROUTES.SERVICES,
  },
  api_keys: {
    old: OLD_DASHBOARD_ROUTES.SETTINGS,
    new: NEW_DASHBOARD_ROUTES.API_KEYS,
  },
  checkout_customization: {
    old: OLD_DASHBOARD_ROUTES.CHECKOUT_CUSTOMIZATION,
    new: NEW_DASHBOARD_ROUTES.CHECKOUT_CUSTOMIZATION,
  },
  payment_integrations: {
    old: OLD_DASHBOARD_ROUTES.PAYMENT_INTEGRATIONS,
    new: NEW_DASHBOARD_ROUTES.PAYMENT_INTEGRATIONS,
  },
  single_payment_integration: {
    old: OLD_DASHBOARD_ROUTES.PAYMENT_INTEGRATIONS,
    new: NEW_DASHBOARD_ROUTES.SINGLE_PAYMENT_INTEGRATION,
  },
  iframes: {
    old: OLD_DASHBOARD_ROUTES.IFRAMES,
    new: NEW_DASHBOARD_ROUTES.IFRAMES,
  },
  xero: {
    old: OLD_DASHBOARD_ROUTES.XERO,
    new: NEW_DASHBOARD_ROUTES.XERO,
  },
};

export const getDashboardsCorrespondingRoute = (
  inputRoute: string, // should be passed with it's leading slash
  targetVersion: TDashboardVersion
): string => {
  const sourceVersion: TDashboardVersion = targetVersion === 'old' ? 'new' : 'old';

  for (const routeMapping of Object.values(ROUTE_MAPPINGS)) {
    const sourcePattern = routeMapping[sourceVersion];
    const match = matchPath({ path: sourcePattern, end: true }, inputRoute);

    if (match) {
      const matchParams = match.params || {};
      const matchParamsKeys = Object.keys(matchParams);
      let targetRoute = routeMapping[targetVersion];

      if (matchParams && !!matchParamsKeys.length) {
        matchParamsKeys.forEach((paramKey) => {
          const paramValue = matchParams[paramKey] || '';
          targetRoute = targetRoute.replace(`:${paramKey}`, paramValue);
        });
      }

      return targetRoute;
    }
  }

  return DEFAULT_ROUTE;
};